import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsService, SystemSettings } from '../services/settings.service';
import { useToast } from '@/components/ui';

export function useSystemSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: settings, isLoading: isLoadingGeneral } = useQuery({
    queryKey: ['admin', 'settings', 'general'],
    queryFn: () => settingsService.getGeneralSettings(),
  });

  const { data: security, isLoading: isLoadingSecurity } = useQuery({
    queryKey: ['admin', 'settings', 'security'],
    queryFn: () => settingsService.getSecuritySettings(),
  });

  const { data: localization, isLoading: isLoadingLocalization } = useQuery({
    queryKey: ['admin', 'settings', 'localization'],
    queryFn: () => settingsService.getLocalizationSettings(),
  });

  const { data: infra, isLoading: isLoadingInfra } = useQuery({
    queryKey: ['admin', 'settings', 'infra'],
    queryFn: () => settingsService.getInfraSettings(),
  });

  const { data: advanced, isLoading: isLoadingAdvanced } = useQuery({
    queryKey: ['admin', 'settings', 'advanced'],
    queryFn: () => settingsService.getAdvancedSettings(),
  });

  const updateMutation = useMutation({
    mutationFn: (newSettings: SystemSettings) => settingsService.updateGeneralSettings(newSettings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'general'] });
      toast.success('Đã lưu cấu hình hệ thống thành công');
    },
    onError: (error: any) => {
      console.error('[Settings] Update error:', error);
      toast.error('Có lỗi xảy ra khi lưu cấu hình: ' + error.message);
    }
  });

  const updateSecurityMutation = useMutation({
    mutationFn: (newConfig: any) => settingsService.updateSecuritySettings(newConfig),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'security'] });
      toast.success('Đã cập nhật chính sách bảo mật thành công');
    },
    onError: (error: any) => {
      console.error('[Settings] Security update error:', error);
      toast.error('Lỗi khi cập nhật chính sách bảo mật: ' + error.message);
    }
  });

  const updateLocalizationMutation = useMutation({
    mutationFn: (newConfig: any) => settingsService.updateLocalizationSettings(newConfig),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'localization'] });
      toast.success('Đã cập nhật thiết lập vùng miền thành công');
    },
    onError: (error: any) => {
      console.error('[Settings] Localization update error:', error);
      toast.error('Lỗi khi cập nhật vùng miền: ' + error.message);
    }
  });

  const updateInfraMutation = useMutation({
    mutationFn: (newConfig: any) => settingsService.updateInfraSettings(newConfig),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'infra'] });
      toast.success('Đã cập nhật cấu hình hạ tầng thành công');
    },
    onError: (error: any) => {
      console.error('[Settings] Infra update error:', error);
      toast.error('Lỗi khi cập nhật hạ tầng: ' + error.message);
    }
  });

  const updateAdvancedMutation = useMutation({
    mutationFn: (newConfig: any) => settingsService.updateAdvancedSettings(newConfig),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings', 'advanced'] });
      toast.success('Đã cập nhật thiết lập nâng cao thành công');
    },
    onError: (error: any) => {
      console.error('[Settings] Advanced update error:', error);
      toast.error('Lỗi khi cập nhật thiết lập nâng cao: ' + error.message);
    }
  });

  return {
    settings,
    security,
    localization,
    infra,
    advanced,
    isLoading: isLoadingGeneral || isLoadingSecurity || isLoadingLocalization || isLoadingInfra || isLoadingAdvanced,
    updateSettings: updateMutation.mutateAsync,
    updateSecurity: updateSecurityMutation.mutateAsync,
    updateLocalization: updateLocalizationMutation.mutateAsync,
    updateInfra: updateInfraMutation.mutateAsync,
    updateAdvanced: updateAdvancedMutation.mutateAsync,
    isUpdating: updateMutation.isPending || updateSecurityMutation.isPending || updateLocalizationMutation.isPending || updateInfraMutation.isPending || updateAdvancedMutation.isPending,
  };
}
